import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Tooltip,
  Divider,
  Badge,
  Avatar,
  useMediaQuery,
  useTheme
} from '@mui/material';

import { useNavigate, useLocation } from 'react-router-dom';

// ✅ Outlined icon versions
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import MenuIcon from '@mui/icons-material/MenuOutlined';
import FavoriteIcon from '@mui/icons-material/FavoriteBorderOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsNoneOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircleOutlined';

function NavbarBack({ title = '', onToggleSidebar }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();

  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const loginType = localStorage.getItem('loginType');
  const resellerImage = localStorage.getItem('resellerImage');
  const username = localStorage.getItem('username');
  const isProductPage = /^\/product\/[^/]+$/.test(location.pathname);
const isResellerPage = /^\/reseller\/[^/]+$/.test(location.pathname);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.clear();
    setAnchorEl(null);
    window.location.href = '/signin';
  };

  const handleBackClick = () => navigate(-1);
const showBackArrow =
  isMobile ||
  ['/liked-products', '/my-cart', '/notifications'].includes(location.pathname) ||
  isProductPage ||
  isResellerPage;

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const resellerId = localStorage.getItem('resellerId');
        const username = localStorage.getItem('username');
        const recipientId = userId || resellerId || username;

        if (!recipientId) return;

        const res = await fetch(`https://reviewhub-lc.onrender.com/api/notification/${recipientId}`);
        const data = await res.json();

        const unread = data.filter((n) => !n.isRead);
        setUnreadCount(unread.length);
      } catch (err) {
        console.error("Failed to fetch unread notification count:", err);
        setUnreadCount(0);
      }
    };

    if (isLoggedIn) fetchUnreadCount();
  }, [isLoggedIn]);

  return (
    <AppBar
      position="sticky"
      elevation={2}
      sx={{
        backgroundColor: '#fff',
        color: '#444444' ,
        py: isMobile ? 0.5 : 1,
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {/* Left Side */}
        <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1.5}>
          {!isMobile && onToggleSidebar && (
            <IconButton onClick={onToggleSidebar} sx={{ color: '#444444'  }}>
              <MenuIcon />
            </IconButton>
          )}

          <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1.5}>
            {showBackArrow && (
              <IconButton onClick={handleBackClick} sx={{ color: '#444444' }}>
                <ArrowBackIcon sx={{ fontSize: isMobile ? 20 : 28 }} />
              </IconButton>
            )}
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: isMobile ? '1rem' : '1.4rem',
              color: '#444444' ,
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Right Side */}
        <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2}>
          {isLoggedIn && (
            <>
              {location.pathname !== '/liked-products' && (
                <Tooltip title="Liked Products">
                  <IconButton onClick={() => navigate('/liked-products')} sx={{ color: '#444444'  }}>
                    <Badge color="secondary" variant="dot">
                      <FavoriteIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}

              {location.pathname !== '/my-cart' && (
                <Tooltip title="My Cart">
                  <IconButton onClick={() => navigate('/my-cart')} sx={{ color: '#444444'  }}>
                    <Badge color="secondary" variant="dot">
                      <ShoppingCartIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}

              {location.pathname !== '/notifications' && (
                <Tooltip title="Notifications">
                  <IconButton
                    onClick={async () => {
                      try {
                        const userId = localStorage.getItem('userId');
                        const resellerId = localStorage.getItem('resellerId');
                        const username = localStorage.getItem('username');
                        const recipientId = userId || resellerId || username;

                        if (recipientId) {
                          await fetch(
                            `https://reviewhub-lc.onrender.com/api/notification/mark-all-read/${recipientId}`,
                            { method: 'PATCH' }
                          );
                          setUnreadCount(0);
                        }
                      } catch (err) {
                        console.error("Failed to mark notifications as read:", err);
                      }
                      navigate('/notifications');
                    }}
                    sx={{ color:'#444444'  }}
                  >
                    <Badge badgeContent={unreadCount} color="error">
                      <NotificationsIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}

              {/* Account Icon */}
              <Tooltip title="Account Options">
                <IconButton onClick={handleMenu} sx={{ color: '#444444'  }}>
                  <Box display="flex" alignItems="center">
                    {!isMobile && (
                      <Typography sx={{ mr: 1, fontWeight: 500 }}>{username}</Typography>
                    )}
                    {loginType === 'reseller' && resellerImage ? (
                      <Avatar
                        src={resellerImage}
                        sx={{
                          width: isMobile ? 30 : 40,
                          height: isMobile ? 30 : 40,
                          border: '2px solid #444444' ,
                        }}
                      />
                    ) : (
                      <AccountCircleIcon fontSize={isMobile ? 'medium' : 'large'} />
                    )}
                  </Box>
                </IconButton>
              </Tooltip>

              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default NavbarBack;
